import React, { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid2';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Close from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import {
  CardConfig,
  LabelPositionEnum,
  InputAdormentPositionEnum,
  UnitPositionEnum,
  Subtitle,
  UnitSystemEnum,
  SectionUnitsEnum,
} from '@/__generated__/graphql';
import { KpiCard } from '@/modules/KpiTracker/components/KpiCards/KpiCard';
import { getIconNode } from '@/modules/KpiTracker/components/KpiCards/utils';
import { keyFromTitle } from '../../utils';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { AbyssIcon, AbyssIcons } from '@/assets/icons/AbyssIcon';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import { formatEnumForDisplay } from '@/utils';

const SECTION_UNIT_OPTIONS = [
  { label: 'USD', value: SectionUnitsEnum.Usd },
  { label: 'GBP', value: SectionUnitsEnum.Gbp },
  { label: 'Hours', value: SectionUnitsEnum.Hours },
  { label: 'Area', value: SectionUnitsEnum.Area },
];

const ICON_OPTIONS: { label: string; value: AbyssIcons | '' }[] = [
  { label: 'Area Coated', value: 'area-coated' },
  { label: 'Build', value: 'build' },
  { label: 'Calendar', value: 'calendar' },
  { label: 'Clock', value: 'clock' },
  { label: 'Cost', value: 'cost' },
  { label: 'Cube', value: 'cube' },
  { label: 'Delay', value: 'delay' },
  { label: 'Description', value: 'description' },
  { label: 'Gear', value: 'gear' },
  { label: 'Position', value: 'position' },
  { label: 'Status', value: 'status' },
  { label: 'Task', value: 'task' },
  { label: 'Unknown', value: 'unknown' },
];

type CardsConfigTabProps = {
  mode: 'ReadOnly' | 'Edit';
  cards: CardConfig[];
  availableEquations: { key: string; name: string }[];
  onCardsChange: (cards: CardConfig[]) => void;
  originalCards?: CardConfig[];
};

export const CardsConfigTab = ({
  mode,
  cards,
  availableEquations,
  onCardsChange,
  originalCards = [],
}: CardsConfigTabProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [cardToDeleteIndex, setCardToDeleteIndex] = useState<number | null>(null);

  const addNewCard = useCallback(() => {
    const title = 'New KPI Card';
    const newCard: CardConfig = {
      id: generateMongoObjectId(),
      key: keyFromTitle(title),
      title,
      order: cards.length + 1,
      icon: 'unknown',
      equationKey: availableEquations[0]?.key || null,
      aggregation: null,
      subtitleConfig: null,
      unit: null,
    };
    onCardsChange([...cards, newCard]);
  }, [cards, availableEquations, onCardsChange]);

  const updateCard = useCallback(
    (index: number, updates: Partial<CardConfig>) => {
      const card = cards[index];
      const isNewCard = !originalCards.some((original) => original.id === card.id);

      if (isNewCard && updates.title && updates.title !== card.title) {
        updates.key = keyFromTitle(updates.title);
      }

      const updatedCards = cards.map((card, i) => (i === index ? { ...card, ...updates } : card));
      onCardsChange(updatedCards);
    },
    [cards, originalCards, onCardsChange]
  );

  const deleteCard = useCallback(
    (index: number) => {
      const updatedCards = cards.filter((_, i) => i !== index);
      onCardsChange(updatedCards);
    },
    [cards, onCardsChange]
  );

  return (
    <Box p={3}>
      <Stack spacing={2}>
        {mode === 'Edit' && (
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={addNewCard}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add New Card
          </Button>
        )}

        {cards.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No KPI cards configured. {mode === 'Edit' && 'Click "Add New Card" to create one.'}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {cards.map((card, index) => (
              <Card key={card.id || index} variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" fontWeight={500}>
                        {card.title || `Card ${index + 1}`}
                      </Typography>
                      {mode === 'Edit' && (
                        <IconButton
                          onClick={() => {
                            setCardToDeleteIndex(index);
                            setIsDeleteDialogOpen(true);
                          }}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Stack>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Card Title"
                          value={card.title || ''}
                          onChange={(e) => updateCard(index, { title: e.target.value })}
                          disabled={mode === 'ReadOnly'}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Card Key"
                          value={card.key || ''}
                          onChange={(e) => updateCard(index, { key: e.target.value })}
                          disabled={true}
                          helperText={
                            originalCards.some((original) => original.id === card.id)
                              ? 'Read-only for existing cards'
                              : 'Auto-generated from title'
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl
                          fullWidth
                          disabled={mode === 'ReadOnly'}
                          sx={{ position: 'relative' }}
                        >
                          <InputLabel>Icon</InputLabel>

                          <Select
                            value={card.icon || ''}
                            label="Icon"
                            onChange={(e) => updateCard(index, { icon: e.target.value })}
                            renderValue={(selected) => {
                              const option = ICON_OPTIONS.find((opt) => opt.value === selected);

                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 4 }}>
                                  {selected === '' ? (
                                    <Close fontSize="small" />
                                  ) : (
                                    <AbyssIcon iconName={selected as AbyssIcons} size="sm" />
                                  )}
                                  {option?.label || selected}
                                </Box>
                              );
                            }}
                          >
                            {ICON_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {option.value === '' ? (
                                    <Close fontSize="small" />
                                  ) : (
                                    <AbyssIcon iconName={option.value} size="sm" />
                                  )}
                                  {option.label}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>

                          {card.icon && mode !== 'ReadOnly' && (
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                right: 32,
                                top: '50%',
                                transform: 'translateY(-50%)',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                updateCard(index, { icon: '' });
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Display Order"
                          value={card.order || 0}
                          onChange={(e) =>
                            updateCard(index, { order: Number.parseInt(e.target.value) })
                          }
                          disabled={mode === 'ReadOnly'}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth disabled={mode === 'ReadOnly'}>
                          <InputLabel>Equation Key</InputLabel>
                          <Select
                            value={card.equationKey || ''}
                            label="Equation Key"
                            onChange={(e) =>
                              updateCard(index, {
                                equationKey: e.target.value || null,
                              })
                            }
                          >
                            <MenuItem value="">
                              <em>No Equation</em>
                            </MenuItem>
                            {availableEquations.map((equation) => (
                              <MenuItem key={equation.key} value={equation.key}>
                                {equation.name || equation.key}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth disabled={mode === 'ReadOnly'}>
                          <InputLabel>Title Unit</InputLabel>
                          <Select
                            value={card.unit || ''}
                            label="Title Unit"
                            onChange={(e) =>
                              updateCard(index, {
                                unit: (e.target.value as SectionUnitsEnum) || null,
                              })
                            }
                          >
                            <MenuItem value="">
                              <em>No Unit</em>
                            </MenuItem>
                            {SECTION_UNIT_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth disabled={mode === 'ReadOnly'}>
                          <InputLabel>Unit Position</InputLabel>
                          <Select
                            value={card.unitPosition || UnitPositionEnum.End}
                            label="Unit Position"
                            onChange={(e) =>
                              updateCard(index, {
                                unitPosition: e.target.value as UnitPositionEnum,
                              })
                            }
                          >
                            {Object.values(UnitPositionEnum).map((position) => (
                              <MenuItem key={position} value={position}>
                                {formatEnumForDisplay(position)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Round to Decimal (Optional)"
                          value={card.roundToDecimal ?? ''}
                          onChange={(e) =>
                            updateCard(index, {
                              roundToDecimal: e.target.value
                                ? Number.parseInt(e.target.value)
                                : null,
                            })
                          }
                          disabled={mode === 'ReadOnly'}
                          slotProps={{ htmlInput: { min: 0, max: 10 } }}
                          helperText="Number of decimal places to round to (0-10)"
                        />
                      </Grid>
                    </Grid>

                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Subtitle Configuration
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              label="Subtitle Text"
                              value={card.subtitleConfig?.text || ''}
                              onChange={(e) =>
                                updateCard(index, {
                                  subtitleConfig: {
                                    ...card.subtitleConfig,
                                    text: e.target.value,
                                  },
                                })
                              }
                              disabled={mode === 'ReadOnly'}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth disabled={mode === 'ReadOnly'}>
                              <InputLabel>Subtitle Equation Key</InputLabel>
                              <Select
                                value={card.subtitleConfig?.equationKey || ''}
                                label="Subtitle Equation Key"
                                onChange={(e) =>
                                  updateCard(index, {
                                    subtitleConfig: {
                                      ...card.subtitleConfig,
                                      equationKey: e.target.value || null,
                                    },
                                  })
                                }
                              >
                                <MenuItem value="">
                                  <em>No Equation</em>
                                </MenuItem>
                                {availableEquations.map((equation) => (
                                  <MenuItem key={equation.key} value={equation.key}>
                                    {equation.name || equation.key}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 12 }}>
                            <FormControl
                              fullWidth
                              disabled={mode === 'ReadOnly'}
                              sx={{ position: 'relative' }}
                            >
                              <InputLabel>Subtitle Icon</InputLabel>

                              <Select
                                value={card.subtitleConfig?.icon || ''}
                                label="Subtitle Icon"
                                onChange={(e) =>
                                  updateCard(index, {
                                    subtitleConfig: {
                                      ...card.subtitleConfig,
                                      icon: e.target.value || null,
                                    },
                                  })
                                }
                                renderValue={(selected) => {
                                  const option = ICON_OPTIONS.find((opt) => opt.value === selected);

                                  return (
                                    <Box
                                      sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 4 }}
                                    >
                                      {selected === '' ? (
                                        <Close fontSize="small" />
                                      ) : (
                                        <AbyssIcon iconName={selected as AbyssIcons} size="sm" />
                                      )}
                                      {option?.label || selected}
                                    </Box>
                                  );
                                }}
                              >
                                {ICON_OPTIONS.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {option.value === '' ? (
                                        <Close fontSize="small" />
                                      ) : (
                                        <AbyssIcon iconName={option.value} size="sm" />
                                      )}
                                      {option.label}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>

                              {card.subtitleConfig?.icon && mode !== 'ReadOnly' && (
                                <IconButton
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    right: 32,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateCard(index, {
                                      subtitleConfig: {
                                        ...card.subtitleConfig,
                                        icon: null,
                                      },
                                    });
                                  }}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              )}
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth disabled={mode === 'ReadOnly'}>
                              <InputLabel>Subtitle Position</InputLabel>
                              <Select
                                value={card.subtitleConfig?.position || LabelPositionEnum.Bottom}
                                label="Subtitle Position"
                                onChange={(e) =>
                                  updateCard(index, {
                                    subtitleConfig: {
                                      ...card.subtitleConfig,
                                      position: e.target.value as LabelPositionEnum,
                                    },
                                  })
                                }
                              >
                                {Object.values(LabelPositionEnum).map((position) => (
                                  <MenuItem key={position} value={position}>
                                    {formatEnumForDisplay(position)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth disabled={mode === 'ReadOnly'}>
                              <InputLabel>Icon Position</InputLabel>
                              <Select
                                value={
                                  card.subtitleConfig?.iconPosition ||
                                  InputAdormentPositionEnum.Start
                                }
                                label="Icon Position"
                                onChange={(e) =>
                                  updateCard(index, {
                                    subtitleConfig: {
                                      ...card.subtitleConfig,
                                      iconPosition: e.target.value as InputAdormentPositionEnum,
                                    },
                                  })
                                }
                              >
                                <MenuItem value={InputAdormentPositionEnum.Start}>Start</MenuItem>
                                <MenuItem value={InputAdormentPositionEnum.End}>End</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth disabled={mode === 'ReadOnly'}>
                              <InputLabel>Subtitle Unit</InputLabel>
                              <Select
                                value={card.subtitleConfig?.unit || ''}
                                label="Subtitle Unit"
                                onChange={(e) =>
                                  updateCard(index, {
                                    subtitleConfig: {
                                      ...card.subtitleConfig,
                                      unit: (e.target.value as SectionUnitsEnum) || null,
                                    },
                                  })
                                }
                              >
                                <MenuItem value="">
                                  <em>No Unit</em>
                                </MenuItem>
                                {SECTION_UNIT_OPTIONS.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth disabled={mode === 'ReadOnly'}>
                              <InputLabel>Subtitle Unit Position</InputLabel>
                              <Select
                                value={card.subtitleConfig?.unitPosition || UnitPositionEnum.End}
                                label="Subtitle Unit Position"
                                onChange={(e) =>
                                  updateCard(index, {
                                    subtitleConfig: {
                                      ...card.subtitleConfig,
                                      unitPosition: e.target.value as UnitPositionEnum,
                                    },
                                  })
                                }
                              >
                                {Object.values(UnitPositionEnum).map((position) => (
                                  <MenuItem key={position} value={position}>
                                    {formatEnumForDisplay(position)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Subtitle Round to Decimal (Optional)"
                              value={card.subtitleConfig?.roundToDecimal ?? ''}
                              onChange={(e) =>
                                updateCard(index, {
                                  subtitleConfig: {
                                    ...card.subtitleConfig,
                                    roundToDecimal: e.target.value
                                      ? Number.parseInt(e.target.value)
                                      : null,
                                  },
                                })
                              }
                              disabled={mode === 'ReadOnly'}
                              slotProps={{ htmlInput: { min: 0, max: 10 } }}
                              helperText="Number of decimal places to round subtitle to (0-10)"
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>

                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        Preview:
                      </Typography>
                      <Box sx={{ maxWidth: 300 }}>
                        <KpiCard
                          title={card.title || 'Sample Card'}
                          value="100"
                          icon={getIconNode(card.icon)}
                          subtitle={
                            card.subtitleConfig
                              ? ({
                                  ...(card.subtitleConfig as Subtitle),
                                  value: 100,
                                } as Subtitle)
                              : undefined
                          }
                          unit={card.unit ?? undefined}
                          unitPosition={card.unitPosition ?? UnitPositionEnum.End}
                          sectionUnit={card.unit ?? undefined}
                          unitSystem={UnitSystemEnum.Metric}
                        />
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
      {isDeleteDialogOpen && (
        <GenericDialog
          open={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setCardToDeleteIndex(null);
          }}
          title="Delete Card"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this card?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setCardToDeleteIndex(null);
                }}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (cardToDeleteIndex !== null) {
                    deleteCard(cardToDeleteIndex);
                  }
                  setIsDeleteDialogOpen(false);
                  setCardToDeleteIndex(null);
                }}
                sx={containedButtonStyle}
              >
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </Box>
  );
};
